import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../utils/supabase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Mevcut oturumu kontrol et
    const getSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          setUser(session.user);
          await fetchProfile(session.user.id);
        }
      } catch (error) {
        console.error('Session error:', error);
      } finally {
        setLoading(false);
      }
    };

    getSession();

    // Auth state değişikliklerini dinle
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state change:', event, session?.user?.id);
        
        // Admin sayfalarındaysa normal kullanıcı auth'u ignore et
        if (window.location.pathname.startsWith('/admin')) {
          console.log('Ignoring auth state change on admin page');
          setLoading(false);
          return;
        }
        
        if (session?.user) {
          setUser(session.user);
          await fetchProfile(session.user.id);
        } else {
          setUser(null);
          setProfile(null);
        }
        setLoading(false);
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const fetchProfile = async (userId) => {
    try {
      console.log('Fetching profile for user:', userId);
      
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();
      
      console.log('Profile fetch result:', { data, error });
      
      if (data) {
        setProfile(data);
      } else {
        // Profil yoksa boş profil oluştur
        setProfile({
          id: userId,
          full_name: '',
          phone: '',
          birth_date: null,
          gender: 'male',
          role: 'user'
        });
      }
    } catch (error) {
      console.error('Profile fetch error:', error);
      // Hata olsa bile boş profil göster
      setProfile({
        id: userId,
        full_name: '',
        phone: '',
        birth_date: null,
        gender: 'male',
        role: 'user'
      });
    }
  };

  const signUp = async (email, password, userData = {}) => {
    try {
      setLoading(true);
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: userData.full_name || '',
          },
          emailRedirectTo: undefined // E-posta doğrulamasını atla, profilde uyarı göster
        }
      });
      
      if (error) throw error;
      
      // Kullanıcı başarıyla oluşturulduysa profil kaydını da oluştur
      if (data.user) {
        try {
          // User_profiles kaydı oluştur (trigger çalışmazsa manuel olarak)
          const { error: profileError } = await supabase
            .from('user_profiles')
            .upsert({
              id: data.user.id,
              full_name: userData.full_name || 'Kullanıcı',
              role: 'user',
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            });
          
          if (profileError) {
            console.warn('Profile creation warning:', profileError);
            // Bu hata kritik değil, kullanıcı giriş yapabilir
          }
        } catch (profileCreateError) {
          console.warn('Profile creation failed:', profileCreateError);
          // Bu hata kritik değil, trigger çalışabilir
        }
      }
      
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (email, password) => {
    try {
      setLoading(true);
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      
      if (error) throw error;
      
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      setUser(null);
      setProfile(null);
      return { error: null };
    } catch (error) {
      return { error };
    }
  };

  const resetPassword = async (email) => {
    try {
      const { data, error } = await supabase.auth.resetPasswordForEmail(email);
      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  };

  const resendEmailConfirmation = async () => {
    try {
      if (!user?.email) throw new Error('No user email found');
      
      const { data, error } = await supabase.auth.resend({
        type: 'signup',
        email: user.email,
        options: {
          emailRedirectTo: `${window.location.origin}/auth`
        }
      });
      
      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  };

  const updateProfile = async (updates) => {
    try {
      if (!user) throw new Error('No user logged in');
      
      console.log('Starting profile update for user:', user.id);
      console.log('Updates to apply:', updates);
      
      // Timeout ile upsert deneme
      console.log('Attempting direct upsert with timeout...');
      
      const upsertData = {
        id: user.id,
        ...updates,
        updated_at: new Date().toISOString()
      };
      
      console.log('Upsert data:', upsertData);
      
      let result = null;
      let upsertError = null;
      
      try {
        const upsertPromise = supabase
          .from('user_profiles')
          .upsert(upsertData, { 
            onConflict: 'id',
            ignoreDuplicates: false 
          })
          .select()
          .single();
        
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Upsert timeout')), 8000)
        );
        
        const response = await Promise.race([upsertPromise, timeoutPromise]);
        result = response.data;
        upsertError = response.error;
        
      } catch (timeoutError) {
        console.warn('Upsert timeout, trying alternative approach');
        
        // Fallback: Raw SQL ile deneme
        try {
          const { data: sqlResult, error: sqlError } = await supabase.rpc('upsert_user_profile', {
            user_id: user.id,
            profile_data: upsertData
          });
          
          if (sqlError) throw sqlError;
          
          result = sqlResult;
          console.log('SQL fallback successful');
          
        } catch (sqlFallbackError) {
          console.error('Both upsert and SQL fallback failed');
          // Son çare: Local state'i güncelle
          const mockProfile = { id: user.id, ...updates };
          setProfile(mockProfile);
          return { data: mockProfile, error: null };
        }
      }
      
      console.log('Upsert result:', { result, upsertError });
      
      if (upsertError) {
        console.error('Upsert error:', upsertError);
        console.error('Upsert error details:', upsertError.message, upsertError.code, upsertError.details);
        throw upsertError;
      }
      
      console.log('Profile upserted successfully:', result);
      setProfile(result);
      return { data: result, error: null };
      
    } catch (error) {
      console.error('Profile update failed:', error);
      return { data: null, error };
    }
  };

  const value = {
    user,
    profile,
    loading,
    signUp,
    signIn,
    signOut,
    resetPassword,
    resendEmailConfirmation,
    updateProfile,
    supabase,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 